import { useEffect, useMemo, useState } from 'react';
import api from '../api/apiClient';
import { isTenantDomain } from '../utils/tenant';
import { getApiBaseUrl } from '../utils/apiBase';

const assetBase = () => getApiBaseUrl().replace(/\/api\/?$/, '');

// Only plain hex colours (#abc or #aabbcc) are accepted. Anything else coming
// from the API is ignored so it can never end up in the page's CSS.
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const safeColor = (value) =>
  typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim() : null;

const COLOR_VARS = {
  primary_color: '--org-primary',
  accent_color: '--org-accent',
  chrome_color: '--org-chrome',
  support_color: '--org-support',
};

// login_slides can arrive as an array or as a JSON string.
// Returns [{ eyebrow, title, description, alt, image }] with image as a full URL ('' if none).
const parseLoginSlides = (raw) => {
  let list = raw;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch (error) {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];

  return list
    .filter((slide) => slide && typeof slide === 'object')
    .map((slide) => ({
      eyebrow: typeof slide.eyebrow === 'string' ? slide.eyebrow : '',
      title: typeof slide.title === 'string' ? slide.title : '',
      description: typeof slide.description === 'string' ? slide.description : '',
      alt: typeof slide.alt === 'string' ? slide.alt : '',
      image: slide.image_path ? `${assetBase()}/${slide.image_path}` : '',
    }));
};

// Returns:
//   onTenant     true on a client subdomain (igi.ethosboard.com)
//   loading      true while the tenant is being looked up
//   notFound     true when the subdomain doesn't match any organization
//   tenant       { name, subdomain, logo_path, colours... } or null
//   logoUrl      full URL of the tenant logo, or '' if none
//   loginSlides  the organization's login carousel slides, or [] to use the defaults
export default function useTenant() {
  const onTenant = isTenantDomain();
  const [state, setState] = useState({
    loading: onTenant,
    tenant: null,
    notFound: false,
  });

  useEffect(() => {
    if (!onTenant) return undefined;

    let cancelled = false;
    const root = document.documentElement;
    const applied = [];

    api.public
      .getTenant()
      .then((res) => {
        if (cancelled) return;
        const tenant = res.data?.data || null;

        // Brand the page with the client's (validated) colours
        if (tenant) {
          Object.entries(COLOR_VARS).forEach(([field, cssVar]) => {
            const color = safeColor(tenant[field]);
            if (color) {
              root.style.setProperty(cssVar, color);
              applied.push(cssVar);
            }
          });
        }

        setState({ loading: false, tenant, notFound: !tenant });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          tenant: null,
          notFound: err?.response?.status === 404,
        });
      });

    return () => {
      cancelled = true;
      applied.forEach((cssVar) => root.style.removeProperty(cssVar));
    };
  }, [onTenant]);

  const logoUrl = state.tenant?.logo_path ? `${assetBase()}/${state.tenant.logo_path}` : '';

  // Memoized so the carousel doesn't restart on every render
  const loginSlides = useMemo(
    () => parseLoginSlides(state.tenant?.login_slides),
    [state.tenant]
  );

  return { ...state, onTenant, logoUrl, loginSlides };
}